import styled from "styled-components";
import { TablePageContainer } from "components/TablePageLayout";
import { pxToRem, withAlpha } from "../../styles/utils";

export const SettingsPageContainer = styled(TablePageContainer)``;

export const PageContent = styled.div`
  min-width: 0;
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  box-shadow: ${({ theme }) => theme.shadows.md}, ${({ theme }) => theme.shadows.ring};

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ContentInner = styled.div`
  width: 100%;
  max-width: ${pxToRem(1180)};
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.xxl)} ${pxToRem(48)};

  @media (max-width: 900px) {
    padding: 0 ${({ theme }) => pxToRem(theme.spacing.md)} ${({ theme }) => pxToRem(theme.spacing.xxxl)};
  }
`;

export const PageTitle = styled.h1`
  position: sticky;
  top: 0;
  z-index: 6;
  margin: 0;
  padding: ${({ theme }) => pxToRem(theme.spacing.xxl)} 0 ${({ theme }) => pxToRem(theme.spacing.xs)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xxl)};
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
  isolation: isolate;

  @media (max-width: 900px) {
    padding-top: ${({ theme }) => pxToRem(theme.spacing.lg)};
  }
`;

export const PageLead = styled.p`
  position: sticky;
  top: ${({ theme }) => pxToRem(theme.spacing.xxl + theme.fontSize.xxl * 1.2 + theme.spacing.xs)};
  z-index: 5;
  margin: 0;
  padding: 0 0 ${({ theme }) => pxToRem(theme.spacing.xs)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;

  @media (max-width: 900px) {
    top: ${({ theme }) => pxToRem(theme.spacing.lg + theme.fontSize.xxl * 1.2 + theme.spacing.xs)};
  }
`;

export const SettingsSection = styled.section`
  display: grid;
  grid-template-columns: ${pxToRem(230)} minmax(0, 1fr);
  gap: ${({ theme }) => pxToRem(theme.spacing.xxl)};
  padding: ${({ theme }) => pxToRem(theme.spacing.xxl)} 0;
  border-top: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => pxToRem(theme.spacing.md)};
  }
`;

export const SectionAside = styled.div`
  h2 {
    margin: 0 0 ${({ theme }) => pxToRem(theme.spacing.xs)};
    color: ${({ theme }) => theme.colors.ink};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
    font-weight: 600;
  }

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.inkSoft};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
    line-height: 1.45;
  }
`;

export const SectionBody = styled.div`
  width: 100%;
  min-width: 0;
`;

export const SettingsRow = styled.div`
  display: grid;
  grid-template-columns: ${pxToRem(140)} minmax(0, 1fr);
  gap: ${({ theme }) => pxToRem(theme.spacing.lg)};
  align-items: start;
  padding: ${({ theme }) => pxToRem(theme.spacing.md)} 0;
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  }
`;

export const RowLabel = styled.div<{ $tight?: boolean }>`
  padding-top: ${({ $tight, theme }) => ($tight ? 0 : pxToRem(theme.spacing.sm))};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.4;

  @media (max-width: 900px) {
    padding-top: 0;
  }
`;

export const Control = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
`;

export const NameFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  width: 100%;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

export const Input = styled.input<{
  $error?: boolean;
  $hasPrefix?: boolean;
  $hasAction?: boolean;
}>`
  width: 100%;
  height: ${({ theme }) => pxToRem(theme.controlHeight.md)};
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  padding-left: ${({ $hasPrefix, theme }) =>
    $hasPrefix ? pxToRem(theme.spacing.xxl) : pxToRem(theme.spacing.sm)};
  padding-right: ${({ $hasAction, theme }) =>
    $hasAction ? pxToRem(48) : pxToRem(theme.spacing.sm)};
  border: ${pxToRem(1)} solid
    ${({ $error, theme }) =>
      $error ? theme.colors.dangerText : withAlpha(theme.colors.ink, 0.26)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.ink};
  font: inherit;
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 400;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.inkMuted};
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => withAlpha(theme.colors.ink, 0.38)};
  }

  &:focus,
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.brand};
    box-shadow: inset 0 0 0 ${pxToRem(1)} ${({ theme }) => theme.colors.brand};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const Prefix = styled.span`
  position: absolute;
  left: ${({ theme }) => pxToRem(theme.spacing.sm)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  pointer-events: none;
`;

export const PeekButton = styled.button`
  position: absolute;
  right: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  padding: 0;
  border: 0;
  border-radius: ${({ theme }) => pxToRem(theme.radii.sm)};
  background: transparent;
  color: ${({ theme }) => theme.colors.inkMuted};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.inkSurface};
    color: ${({ theme }) => theme.colors.ink};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(1)};
  }

  svg {
    width: ${pxToRem(18)};
    height: ${pxToRem(18)};
  }
`;

export const Hint = styled.div<{ $error?: boolean; $brand?: boolean }>`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xs)};
  color: ${({ $error, $brand, theme }) =>
    $error ? theme.colors.dangerText : $brand ? theme.colors.brand : theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.4;
`;

export const CodeHint = styled(Hint)`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => pxToRem(theme.spacing.xxs)};
`;

export const ResendButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  color: ${({ theme }) => theme.colors.brand};
  font: inherit;
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const StatusLine = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  padding-top: ${pxToRem(2)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
`;

export const StatusDot = styled.span<{ $connected: boolean }>`
  width: ${pxToRem(8)};
  height: ${pxToRem(8)};
  border-radius: 50%;
  background: ${({ $connected, theme }) =>
    $connected ? theme.colors.success : withAlpha(theme.colors.ink, 0.22)};
`;

export const StatusNote = styled.div`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.4;
`;

export const ActionGroup = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: nowrap;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};

  > button {
    width: auto;
    min-width: ${pxToRem(100)};
    max-width: none;
    flex: 0 0 auto;
  }

  @media (max-width: 900px) {
    flex-wrap: wrap;
  }
`;

export const InstructionHint = styled(Hint)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => pxToRem(theme.spacing.xxs)};
`;

export const InstructionLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  color: ${({ theme }) => theme.colors.brand};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }

  svg {
    width: ${pxToRem(15)};
    height: ${pxToRem(15)};
  }
`;

export const ErrorSummary = styled.div`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xs)};
  color: ${({ theme }) => theme.colors.dangerText};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
`;

export const LoadingState = styled.div`
  padding: ${({ theme }) => pxToRem(theme.spacing.xxl)} 0;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
`;
